import { Injectable } from '@angular/core';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { WindowRefService } from '@services/window-ref-service';
import { ConnectionService } from '@services/connection-service';
import { AppConfig } from 'client/app/app.config';
import {
  RegimenRehaulV1Experiment,
  RegimenRehaulV1Result,
  RestrictedProductForPregnancyAndTeenagerExperiment,
  RegimenReorderExperiment,
  ReviewOrderPageExperiment,
  RegimenPricingExperiment,
  BottomNavConfigExperiment,
  ShopDynamicBannerExperiment,
  ReportRootCauseExperiment,
  ChatSkipLastQuestionsExperiment
} from './experiment.service.types';


@Injectable({ providedIn: 'root' })
export class ExperimentService {
  appVersion: number = null;
  webAppVersion: string = null;
  private experimentAndroidVersionForParseInterceptor: number = 207000;
  private experimentForVideoInstructions: number = 207141;
  isInternalUser: boolean = false;
  appVersionFromConfigTable: { androidApp: string, webApp: string } = {
    androidApp: null, webApp: null,
  };
  isMulticoncernUI: boolean = false;
  acqRebranding2025: boolean = false;

  // Experiment flags
  applyCashExperiment: boolean = false;
  enableHighlightIO: boolean = false;
  globalCacheExperiment: any = null; // Store the experiment object for setEnableGlobalCache
  onboardingBackButtonChangesEnabled: boolean = false;
  restrictedShopForPregnancyAndTeenagerExperiment: RestrictedProductForPregnancyAndTeenagerExperiment = null;
  addToCartVariantSelection: boolean = false;
  genderTranslationsEnabled: boolean = false;
  hideIncompleteChatBottomsheet: boolean = false;
  optimiseApiEnabled: boolean = false
  sidePhotoNudgeInFrontFace: Record<string, unknown> = null;
  showRebrandedSkinParameters: boolean = false;
  videoCallExperiment: boolean = false;
  regimenReorderExperiment: RegimenReorderExperiment = null;
  reviewOrderPageExperiment: ReviewOrderPageExperiment = null;
  shopDynamicBannerExperiment: ShopDynamicBannerExperiment = null;
  prefillAddressShiprocketEnabled: boolean = false;
  newRegimenPricingExperiment: RegimenPricingExperiment;
  bottomNavConfigExperiment: BottomNavConfigExperiment = null;
  acqCopyExperiment: boolean = false;


  // Skin/Hair report experiment flags
  skinReportRegimenAndSkinParamsMapping: boolean = false;
  skinReportDrVideoExperiment: any = null;
  skinReportHeroIngredientsVariant: { numberOfIngredients: number } = null;
  skinReportNewDrUiEnabled: boolean = false;
  treatmentPlanDescriptionExperiment: any = null;
  newHairReportExp: boolean = false;
  chatSkipLastQuestionsExperiment: ChatSkipLastQuestionsExperiment = null;
  chatSkipLastQuestionHomeBanner: ChatSkipLastQuestionsExperiment = null;
  rootCausesExperiment: ReportRootCauseExperiment = null;

  constructor(
    private readonly appWebBridge: AppWebBridgeService,
    private readonly windowRef: WindowRefService,
    private readonly conn: ConnectionService,
    private readonly appConfig: AppConfig,
  ) {
    this.appWebBridge.requestAppVersion().then((version: number) => (this.appVersion = version));
    try {
      this.webAppVersion = this.appConfig.Shared.AppVersion;
      this.isInternalUser = this.conn.isInternalUser();
    } catch (err) {
      // Handle error here
    }
  }

  async enableParseInterceptor(): Promise<boolean> {
    const os = await this.appWebBridge.requestOSInformation();
    const isiOSDevice = os === 'iOS' || this.windowRef.isSafariBrowser();
    if (isiOSDevice) return true;
    if (this.isInternalUser) return true;
    if (this.appVersion >= this.experimentAndroidVersionForParseInterceptor) return true;
    return false;
  }

  get showNewInstructionVideo(): boolean {
    return this.appVersion >= this.experimentForVideoInstructions;
  }

  /**
   * Fetches and processes active user experiments
   * This function can be used to centralize experiment handling logic
   * @param username Optional username to fetch experiments for
   * @returns Promise<any[]> The fetched experiments array
   */
  async fetchActiveUserExperiment(username?: string): Promise<any[]> {
    try {
      const experiments = await this.conn.findUserActiveExperiments(username);
      // Reset experiment flags
      this.resetExperimentFlags();

      // Process experiments
      experiments?.forEach((experiment: any): void => {
        this.processExperiment(experiment);
      });

      return experiments || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Resets all experiment flags to their default values
   */
  private resetExperimentFlags(): void {
    this.applyCashExperiment = false;
    this.enableHighlightIO = false;
    this.globalCacheExperiment = null;
    this.onboardingBackButtonChangesEnabled = false;
    this.addToCartVariantSelection = false;
    this.genderTranslationsEnabled = false;
    this.videoCallExperiment = false;
    this.regimenReorderExperiment = null;
    this.reviewOrderPageExperiment = null;
    this.skinReportRegimenAndSkinParamsMapping = false;
    this.skinReportDrVideoExperiment = null;
    this.skinReportHeroIngredientsVariant = null;
    this.skinReportNewDrUiEnabled = false;
    this.treatmentPlanDescriptionExperiment = null;
    this.prefillAddressShiprocketEnabled = false;
    this.newRegimenPricingExperiment = undefined;
    this.rootCausesExperiment = null;
    this.bottomNavConfigExperiment = null;
    this.shopDynamicBannerExperiment = null;
    this.acqCopyExperiment = null;
    this.rootCausesExperiment = null;
  }

  /**
   * Processes individual experiment and sets appropriate flags
   * @param experiment The experiment object to process
   */
  private processExperiment(experiment: any): void {
    switch (experiment.key) {
      case 'apply_cash':
        this.applyCashExperiment = true;
        break;
      case 'enable_highlight_io':
        this.enableHighlightIO = true;
        break;
      case 'enable_global_cache':
        this.globalCacheExperiment = experiment; // Store the full experiment object
        break;
      case 'acq_rebranding_2025':
        this.acqRebranding2025 = true;
        break;
      case 'onboarding_back_button_changes':
        this.onboardingBackButtonChangesEnabled = true;
        break;
      case 'add_to_cart_variant_selection':
        this.addToCartVariantSelection = true;
        break;
      case 'shop_restricted_product_pregnancy_teenager':
        this.restrictedShopForPregnancyAndTeenagerExperiment = experiment;
        break;
      case 'gender_translations':
        this.genderTranslationsEnabled = true;
        break;
      case 'hide_incomplete_chat_bottomsheet':
        this.hideIncompleteChatBottomsheet = true;
        break;
      case 'optimise_paid_home_page':
        this.optimiseApiEnabled = true;
        break;
      case 'side_photo_nudge_in_front':
        this.sidePhotoNudgeInFrontFace = experiment;
        break;
      case 'skin_report_params_UI_bar':
        this.showRebrandedSkinParameters = true;
        break;
      case '3000_video_call':
        this.videoCallExperiment = true;
        break;
      case 'skin_report_regimen_and_skin_params_mapping':
        this.skinReportRegimenAndSkinParamsMapping = true;
        break;
      case 'skin_report_dr_video':
        this.skinReportDrVideoExperiment = experiment;
        break;
      case 'skin_report_hero_ingredients':
        this.skinReportHeroIngredientsVariant = experiment?.variant;
        break;
      case 'skin_report_new_Dr_UI':
        this.skinReportNewDrUiEnabled = true;
        break;
      case 'treatment_plan_description':
        this.treatmentPlanDescriptionExperiment = experiment;
        break;
      case 'kit_reorder':
        this.regimenReorderExperiment = experiment;
        break;
      case 'review_order_page':
        this.reviewOrderPageExperiment = experiment;
        break;
      case 'new_hair_report':
        this.newHairReportExp = experiment.enable;
        break;
      case 'prefill_address_shiprocket':
        this.prefillAddressShiprocketEnabled = true;
        break;
      case 'add_extra_regimen_products':
        this.newRegimenPricingExperiment = experiment;
        break;
      case 'acq_copy_experiment':
        this.acqCopyExperiment = experiment?.enable;
        break;
      case 'report_root_causes':
        this.rootCausesExperiment = experiment;
        break;
      case 'bottom_nav_config':
        this.bottomNavConfigExperiment = experiment;
        break;
      case 'shop_dynamic_banner':
        this.shopDynamicBannerExperiment = experiment;
        break;
      case 'chat_skip_last_questions':
        this.chatSkipLastQuestionsExperiment = experiment;
        break;
      case 'chat_skip_last_questions_home_banner':
        this.chatSkipLastQuestionHomeBanner = experiment;
        break;
      default:
        break;
    }
  }

  getRootCausesExperiment(): ReportRootCauseExperiment {
    return this.rootCausesExperiment;
  }

  isOptimiseApiEnabled(): boolean {
    return this.optimiseApiEnabled;
  }

  getRestrictedShopExperiment(): RestrictedProductForPregnancyAndTeenagerExperiment {
    return this.restrictedShopForPregnancyAndTeenagerExperiment ?? null;
  }

  getChatSkipLastQuestionsExperiment() {
    return this.chatSkipLastQuestionsExperiment ?? null;
  }

  getChatSkipLastQuestionHomeBanner() {
    return this.chatSkipLastQuestionHomeBanner ?? null;
  }

  shouldShowRebrandedSkinParameters(): boolean {
    return this.showRebrandedSkinParameters;
  }

  shouldShowNewHairReportExp(): boolean {
    return this.newHairReportExp;
  }

  processRegimenRehaulV1Experiment(
    experiment: RegimenRehaulV1Experiment,
    options?: { regimenClass?: string; userLanguage?: string; }
  ): RegimenRehaulV1Result {
    if (!experiment || !experiment.variant) {
      return {};
    }

    const result: RegimenRehaulV1Result = {};
    const variants = experiment.variant;

    if (variants.back_nudge_dr_call !== undefined) {
      result.nudgeDoctorCallOnBack = variants.back_nudge_dr_call;
    }

    if (variants.dr_banner_change !== undefined) {
      result.drBannerChange = variants.dr_banner_change;
    }

    if (variants.remove_variant !== undefined) {
      result.removeVariants = variants.remove_variant;
    }

    return result;
  }

  /**
   * Calculates whether to use CureSkin Cash based on experiment and user orderState
   * - If applyCashExperiment is false: always return true
   * - If applyCashExperiment is true:
   *   - If orderState is DELIVERED: return false
   *   - Otherwise: return true
   * @param orderState The user's orderState (e.g., 'DELIVERED', 'PROCESSING', etc.)
   * @returns boolean indicating whether to use CureSkin Cash
   */
  shouldUseCureSkinCash(orderState?: string): boolean {
    // If experiment is off, always use cash
    if (!this.applyCashExperiment) {
      return true;
    }

    // If experiment is on, check orderState
    // If orderState is DELIVERED, don't use cash
    if (orderState === this.appConfig.Shared.User.OrderState.DELIVERED) {
      return false;
    }

    // For any other orderState, use cash
    return true;
  }

  getBottomNavConfig(): BottomNavConfigExperiment {
    return this.bottomNavConfigExperiment;
  }

  isBottomNavConfigActive(): boolean {
    const exp = this.bottomNavConfigExperiment;
    return !!(exp?.enable && exp?.variant);
  }

  get sidePhotoNudgeInFrontFaceExperiment(): Record<string, unknown> {
    return this.sidePhotoNudgeInFrontFace ?? null;
  }

  isSkinReportNewDrUiEnabled(): boolean {
    return this.skinReportNewDrUiEnabled;
  }

  isSkinReportRegimenAndSkinParamsMapping(): boolean {
    return this.skinReportRegimenAndSkinParamsMapping;
  }

  getSkinReportDrVideoExperiment(): any {
    return this.skinReportDrVideoExperiment;
  }

  getSkinReportHeroIngredientsVariant(): { numberOfIngredients: number } {
    return this.skinReportHeroIngredientsVariant;
  }

  getShopDynamicBannerExperiment(): ShopDynamicBannerExperiment {
    return this.shopDynamicBannerExperiment ?? null;
  }
}
